import Papa from 'papaparse'
import { v4 as uuidv4 } from 'uuid'
import Vue from 'vue'
import Vuex from 'vuex'
import i18n from '../../VueI18n.js'
import { getAllEspecies } from '../../services/requests/EspecieRequests.js'
import { getAllMaterialGeneticos } from '../../services/requests/MaterialGeneticoRequests.js'
import { useMessagesStore } from './moduleMessages.js'

const messagesStore = useMessagesStore()

const state = {
	file: undefined,
	medicoesHistoricas: [],
	descricao: undefined,
	colunaAtributo: [],
	materiaisGeneticos: [],
	especies: [],
	variavelBiometrica: 'vtcc',

	fazendasSelecionadas: [],
	talhoesSelecionados: [],
	parcelasSelecionadas: [],
}
const mutations = {
	changeFazendasSelecionadas(state, payload) {
		if (state.fazendasSelecionadas.includes(payload)) {
			state.fazendasSelecionadas.splice(state.fazendasSelecionadas.indexOf(payload), 1)
		} else {
			state.fazendasSelecionadas.push(payload)
		}
	},
	changeTalhoesSelecionados(state, payload) {
		if (state.talhoesSelecionados.includes(payload)) {
			state.talhoesSelecionados.splice(state.talhoesSelecionados.indexOf(payload), 1)
		} else {
			state.talhoesSelecionados.push(payload)
		}
	},
	changeParcelasSelecionadas(state, payload) {
		if (state.parcelasSelecionadas.includes(payload)) {
			state.parcelasSelecionadas.splice(state.parcelasSelecionadas.indexOf(payload), 1)
		} else {
			state.parcelasSelecionadas.push(payload)
		}
	},
	setDescricao(state, payload) {
		state.descricao = payload
	},
	setMedicoesHistoricas(state, payload) {
		payload = payload.map((medicao) => {
			return { ...medicao }
		})
		state.medicoesHistoricas = payload
	},
	setUploadedFile(state, payload) {
		state.file = payload
	},
	setColunaAtributo(state, payload) {
		state.colunaAtributo = payload
	},
	setVariavelBiometrica(state, payload) {
		state.variavelBiometrica = payload
	},
	async setEspecies(state) {
		const especies = await getAllEspecies()
		state.especies = especies.data.result.map((especie) => especie.espe_ds_nome)
	},
	async setMateriaisGeneticos(state) {
		const materiaisGeneticos = await getAllMaterialGeneticos()
		state.materiaisGeneticos = materiaisGeneticos.data.result.map(
			(materialGenetico) => materialGenetico.mtge_cd_codigo,
		)
	},
	changeRow({ medicoesHistoricas }, { id, key, value }) {
		const row = medicoesHistoricas.find((medicao) => medicao.id === id)
		medicoesHistoricas.splice(medicoesHistoricas.indexOf(row), 1, { ...row, [key]: value })
	},
	dropRow({ medicoesHistoricas }, medicaoId) {
		const row = medicoesHistoricas.find((medicao) => medicao.id === medicaoId)
		medicoesHistoricas.splice(medicoesHistoricas.indexOf(row), 1)
	},
}
const actions = {
	handleFileUpload({ commit, dispatch }, files) {
		if (files[0].size / 1000000 > 1.5) {
			messagesStore.commit('mutationShowMessage', {
				messageTitle: i18n.t('error'),
				messageText:
					'Não foi possível finalizar o carregamento. Arquivo ultrapassa o limite determinado (1.5MB)',
				messageType: 'error',
			})

			commit('setUploadedFile', undefined)
		} else {
			commit('setUploadedFile', [...files])

			let reader = new FileReader()

			reader.readAsText(files[0], 'ISO-8859-1')

			reader.onload = function (event) {
				Papa.parse(event.target.result, {
					header: true,
					skipEmptyLines: true,
					complete: function (results) {
						let data = results.data.map((medicao) => {
							return { ...medicao, id: uuidv4(), ['Não Atribuir']: null }
						})

						// Seta um id de parcela unico para cada grupo de parcelas, este id unico é importante
						// para gerar modelos de classificacao de sitio e prognose que precisam de remedicao de parcela
						let parcelasAgrupadas = Object.groupBy(
							data,
							(item) => `${item.Estrato}-${item.Talhao}-${item.Plantio}-${item.Parcela}`,
						)
						for (let parcelas in parcelasAgrupadas) {
							const parcelaId = uuidv4()
							if (Object.prototype.hasOwnProperty.call(parcelasAgrupadas, parcelas)) {
								parcelasAgrupadas[parcelas] = parcelasAgrupadas[parcelas].map((parcela) => ({
									...parcela,
									ParcelaId: parcelaId,
								}))
							}
						}

						// Transforma o objeto agrupado em lista novamente
						data = Object.values(parcelasAgrupadas).reduce(
							(parcelas, parcela) => parcelas.concat(parcela),
							[],
						)

						dispatch('handleMedicoesHistoricas', data)
					}.bind(this),
				})
			}.bind(this)
		}
	},

	handleMedicoesHistoricas({ commit, getters, state }, medicoesHistoricas) {
		commit('setMedicoesHistoricas', medicoesHistoricas)

		const colunaAtributo = getters['colunasMapeamento']
			.concat(getters['colunasProcessamento'])
			.map((coluna) => {
				return {
					coluna: coluna.nome,
					value: coluna.key,
					key:
						Object.keys(state.medicoesHistoricas[0])
							.map((m) => {
								return { value: m, label: m }
							})
							.find((f) => new RegExp(coluna.regex).test(f.label))?.label || 'Não Atribuir',
				}
			})

		commit('setColunaAtributo', colunaAtributo)
	},
}
const getters = {
	colunasMapeamento({ especies, materiaisGeneticos }) {
		return [
			{
				nome: i18n.t('farm'),
				key: 'fazenda',
				obrigatorio: true,
				descricao: i18n.t('deveConterFazenda'),
				exemplo: 'Fazenda 1',
				formato: 'text',
				regex: new RegExp('f.*z', 'gi'),
				validator: (value) => {
					return typeof value === 'string' || value === null
				},
			},
			{
				nome: i18n.t('stratum'),
				key: 'estrato',
				obrigatorio: true,
				descricao: i18n.t('deveConterCodigoEstrato'),
				exemplo: 'EF-123',
				formato: 'text',
				regex: new RegExp('estr', 'gi'),
				validator: (value) => {
					return typeof value === 'string' || value === null
				},
			},
			{
				nome: i18n.t('section'),
				key: 'talhao',
				obrigatorio: true,
				descricao: i18n.t('deveConterCodigoTalhao'),
				exemplo: 'T-01',
				formato: 'text',
				regex: new RegExp('t.*l.*h', 'gi'),
				validator: (value) => {
					return typeof value === 'string' || value === null
				},
			},
			{
				nome: i18n.t('plantation'),
				key: 'plantio',
				obrigatorio: true,
				descricao: i18n.t('deveConterCodigoPlantio'),
				exemplo: '001',
				formato: 'number',
				regex: new RegExp('p.*t', 'gi'),
				validator: (value) => {
					return typeof value === 'string' || value === null
				},
			},
			{
				nome: i18n.t('plot'),
				key: 'parcela',
				obrigatorio: true,
				descricao: i18n.t('deveConterCodigoParcela'),
				exemplo: 'P-01',
				formato: 'text',
				regex: new RegExp('p.*r.*c.*l', 'gi'),
				validator: (value) => {
					return typeof value === 'string' && value != 'Não Atribuído'
				},
			},
			{
				nome: i18n.t('plantationDate'),
				key: 'dataPlantio',
				descricao: i18n.t('deveConterDataPlantio'),
				exemplo: 'DD/MM/YYYY',
				formato: 'text',
				regex: new RegExp('d.*t.*p.*l.*t', 'gi'),
				validator: (value) => {
					const date = value.split('/').reverse().join('/') //se reverter a ordem para ano/mes/dia, o new Date() entende como data correta
					return new Date(date).isValid() || value === null
				},
			},
			{
				nome: i18n.t('measurementDate'),
				key: 'dataMedicao',
				descricao: i18n.t('deveConterDataMedicao'),
				exemplo: 'DD/MM/YYYY',
				formato: 'text',
				regex: new RegExp('d.*t', 'gi'),
				validator: (value) => {
					const date = value.split('/').reverse().join('/') //se reverter a ordem para ano/mes/dia, o new Date() entende como data correta
					return new Date(date).isValid() || value === null
				},
			},
			{
				nome: i18n.t('age'),
				key: 'idade',
				obrigatorio: true,
				descricao: i18n.t('deveConterIdade'),
				exemplo: '1',
				formato: 'number',
				regex: new RegExp('i.*da', 'gi'),
				validator: (value) => {
					return !isNaN(value) && value != 'Não Atribuído'
				},
			},
			{
				nome: i18n.t('spacing'),
				key: 'espacamento',
				descricao: i18n.t('deveConterEspacamento'),
				exemplo: '3,0x2,0',
				formato: 'texto',
				regex: new RegExp('e.*sp.*a', 'gi'),
				validator: (value) => {
					return typeof value === 'string' || value === null
				},
			},
			{
				nome: i18n.t('geneticMaterial'),
				key: 'materialGenetico',
				obrigatorio: true,
				descricao: i18n.t('deveConterMaterialGenetico'),
				exemplo: 'PL-3336',
				formato: 'text',
				regex: new RegExp('mat', 'gi'),
				validator: (value) => {
					return materiaisGeneticos.includes(value) || value === null
				},
			},
			{
				nome: i18n.t('species'),
				key: 'especie',
				obrigatorio: true,
				descricao: i18n.t('deveConterEspecie'),
				exemplo: 'E.urophylla',
				formato: 'text',
				regex: new RegExp('espe', 'gi'),
				validator: (value) => {
					return especies.includes(value) || value === null
				},
			},
			{
				nome: i18n.t('city'),
				key: 'cidade',
				obrigatorio: true,
				descricao: i18n.t('deveConterCidade'),
				exemplo: 'São José',
				formato: 'text',
				regex: new RegExp('cid', 'gi'),
				validator: (value) => {
					return typeof value === 'string' && value != 'Não Atribuído'
				},
			},
			{
				nome: i18n.t('state'),
				key: 'estado',
				obrigatorio: true,
				descricao: i18n.t('deveConterEstado'),
				exemplo: 'SP',
				formato: 'text',
				regex: new RegExp('es.*t.*a.*d', 'gi'),
				validator: (value) => {
					const siglaEstados = [
						'PE',
						'PR',
						'PB',
						'PA',
						'MG',
						'MS',
						'MT',
						'MA',
						'GO',
						'ES',
						'DF',
						'CE',
						'BA',
						'AM',
						'AP',
						'AL',
						'AC',
						'TO',
						'SE',
						'SP',
						'SC',
						'RR',
						'RO',
						'RS',
						'RN',
						'RJ',
						'PI',
					]
					return siglaEstados.includes(value) && value != 'Não Atribuído'
				},
			},
			{
				nome: i18n.t('country'),
				key: 'pais',
				descricao: i18n.t('deveConterPais'),
				exemplo: 'Brasil',
				formato: 'text',
				regex: new RegExp('p.*a.*s', 'gi'),
				validator: (value) => {
					return typeof value === 'string' || value === null
				},
			},
			{
				nome: i18n.t('inventoryType'),
				key: 'tipoInventario',
				obrigatorio: true,
				descricao: i18n.t('deveConterTipoInventario'),
				exemplo: 'IFC',
				formato: 'text',
				regex: new RegExp('inv', 'gi'),
				validator: (value) => {
					return ['IFI', 'IFC', 'IPC'].includes(value)
				},
			},
			{
				nome: i18n.t('management'),
				key: 'manejo',
				descricao: i18n.t('deveConterManejo'),
				exemplo: 'Celulose',
				formato: 'text',
				regex: new RegExp('man', 'gi'),
				validator: (value) => {
					return typeof value === 'string' || value === null
				},
			},
			{
				nome: i18n.t('cicle'),
				key: 'ciclo',
				descricao: i18n.t('deveConter'),
				exemplo: 1,
				formato: 'number',
				regex: new RegExp('cic', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: i18n.t('rotation'),
				key: 'rotacao',
				descricao: i18n.t('deveConter'),
				exemplo: 1,
				formato: 'number',
				regex: new RegExp('rot', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
		]
	},
	colunasProcessamento() {
		return [
			{
				nome: 'VTCC',
				key: 'vtcc',
				descricao: i18n.t('totalVolumeWithBark'),
				label: i18n.t('volumeTotalComCasca'),
				exemplo: '250.153',
				formato: 'number',
				regex: new RegExp('vtc', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'VTSC',
				key: 'vtsc',
				descricao: i18n.t('totalVolumeWithOutBark'),
				label: i18n.t('volumeTotalSemCasca'),
				exemplo: '247.283',
				formato: 'number',
				regex: new RegExp('vts', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'VICC',
				key: 'vicc',
				descricao: i18n.t('volumeIndividualComCasca'),
				label: i18n.t('arvoresMedVolumeTotalComCasca'),
				exemplo: '0.7652',
				formato: 'number',
				regex: new RegExp('vic', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'VISC',
				key: 'visc',
				descricao: i18n.t('volumeIndividualSemCasca'),
				label: i18n.t('arvoresMedVolumeTotalSemCasca'),
				exemplo: '0.7156',
				formato: 'number',
				regex: new RegExp('vis', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'AB',
				key: 'ab',
				descricao: i18n.t('basalArea'),
				label: i18n.t('areaBasal'),
				exemplo: '23.12',
				formato: 'number',
				regex: new RegExp('ab', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'DAP_MAX',
				key: 'dap_max',
				descricao: i18n.t('maximumHBD'),
				label: i18n.t('dapMaximo'),
				exemplo: '30.2',
				formato: 'number',
				regex: new RegExp('dap.*ma', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'DAP_MED',
				key: 'dap_med',
				descricao: i18n.t('averageHBD'),
				label: i18n.t('dapMedio'),
				exemplo: '26.7',
				formato: 'number',
				regex: new RegExp('dap.*me', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'DAP_MIN',
				key: 'dap_min',
				descricao: i18n.t('minimumHBD'),
				label: i18n.t('dapMinimo'),
				exemplo: '25.3',
				formato: 'number',
				regex: new RegExp('dap.*mi', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'DG',
				key: 'dg',
				descricao: i18n.t('squareDiameter'),
				label: i18n.t('dg'),
				exemplo: '28.2',
				formato: 'number',
				regex: new RegExp('dg', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'H_DOM',
				key: 'h_dom',
				descricao: i18n.t('dominantHeight'),
				label: i18n.t('alturaDominante'),
				exemplo: '18.93',
				formato: 'number',
				regex: new RegExp('h.*dom', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'H_MAX',
				key: 'h_max',
				descricao: i18n.t('maximumHeight'),
				label: i18n.t('alturaMaxima'),
				exemplo: '19.92',
				formato: 'number',
				regex: new RegExp('h.*max', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'H_MED',
				key: 'h_med',
				descricao: i18n.t('averageHeight'),
				label: i18n.t('alturaMedia'),
				exemplo: '16.23',
				formato: 'number',
				regex: new RegExp('h.*med', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'H_MIN',
				key: 'h_min',
				descricao: i18n.t('minimumHeight'),
				label: i18n.t('alturaMinima'),
				exemplo: '14.64',
				formato: 'number',
				regex: new RegExp('h.*min', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'N_FUSTES',
				key: 'n_fustes',
				descricao: i18n.t('stemNumber'),
				label: i18n.t('numeroDeFustes'),
				exemplo: 100,
				formato: 'number',
				regex: new RegExp('n.*fus', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
			{
				nome: 'PF_UTEIS',
				key: 'pf_uteis',
				descricao: i18n.t('proporcaoFustesUteis'),
				label: i18n.t('arvoresUteis'),
				exemplo: 95,
				formato: 'number',
				regex: new RegExp('p.*f.*u', 'gi'),
				validator: (value) => {
					return !isNaN(value) || value === null
				},
			},
		]
	},
	dropdownVisualizarDispersao({ colunaAtributo }, { colunasProcessamento }) {
		const colunasKey = colunasProcessamento.map((coluna) => coluna.key)
		return colunaAtributo
			.filter((colunaA) => colunasKey.includes(colunaA.value) && colunaA.key != 'Não Atribuir')
			.map(({ value }) => {
				return {
					label: colunasProcessamento.find((f) => f.key === value).label,
					value,
				}
			})
	},
	linhasInconsistentes(
		{ medicoesHistoricas, colunaAtributo },
		{ colunasMapeamento, colunasProcessamento },
	) {
		const linhas = medicoesHistoricas.filter((data) => {
			const validations = colunasMapeamento.concat(colunasProcessamento).some((coluna) => {
				const { key } = colunaAtributo.find((c) => c.value === coluna.key)
				return !coluna.validator(data[key])
			})
			return validations
		})

		return linhas
	},
	seriesChart({ colunaAtributo, variavelBiometrica }, { medicoesHistoricasFiltradas }) {
		const { key: idade } = colunaAtributo?.find((coluna) => coluna.value === 'idade') || {
			key: 'Idade',
		}
		const coluna = colunaAtributo?.find((coluna) => {
			return coluna.value === variavelBiometrica
		})
		const data = medicoesHistoricasFiltradas.map((medicao) => {
			return {
				...medicao,
				x: Number(medicao[idade]),
				y: Number(Number(medicao[coluna?.key || 'VTCC']).toFixed(4)),
			}
		})

		return [
			{
				type: 'scatter',
				name: 'Serie',
				color: '#009E27',
				id: 'serie',
				data,
				turboThreshold: Number.MAX_SAFE_INTEGER,
				// boostThreshold: 2000,
				animation: false,
				marker: { symbol: 'circle' },
			},
		]
	},
	seriesChartBoxPlot(
		{ medicoesHistoricas, colunaAtributo, variavelBiometrica },
		{ colunasProcessamento, medicoesHistoricasFiltradas },
	) {
		const coluna = colunaAtributo?.find((coluna) => {
			return coluna.value === variavelBiometrica
		})

		const dadosVariavel = medicoesHistoricas
			.map((medicao) => Number(medicao[coluna?.key || 'VTCC']))
			.map((m) => Number(m.toFixed(4)))
			.sort((a, b) => (a > b ? 1 : -1))

		const MEDIANA = dadosVariavel[Math.floor(dadosVariavel.length / 2)]
		const PRIMEIRO_QUARTIL = dadosVariavel[Math.floor(Math.floor(dadosVariavel.length / 2) / 2)]
		const TERCEIRO_QUARTIL =
			dadosVariavel[
				Math.floor(dadosVariavel.length / 2) + Math.floor(Math.floor(dadosVariavel.length / 2) / 2)
			]
		const LAS = TERCEIRO_QUARTIL + 1.5 * (TERCEIRO_QUARTIL - PRIMEIRO_QUARTIL)
		const LAI = PRIMEIRO_QUARTIL - 1.5 * (TERCEIRO_QUARTIL - PRIMEIRO_QUARTIL)

		const data = medicoesHistoricasFiltradas.map((medicao) => {
			return [0, Number(Number(medicao[coluna?.key || 'VTCC']).toFixed(4))]
		})

		return [
			{
				type: 'boxplot',
				name: 'Observações',
				id: 'observ',
				color: 'rgb(255, 0, 0)',
				data: [
					[LAI, PRIMEIRO_QUARTIL, MEDIANA, TERCEIRO_QUARTIL, LAS].map((m) => Number(m.toFixed(4))),
				],
				tooltip: {
					headerFormat: `<b>${colunasProcessamento.find((coluna) => coluna.key === store.state.variavelBiometrica).label}</b><br/><br/>`,
					pointFormat:
						'<b>- Mínimo: </b> {point.low} <br/>' +
						'<b>- Quartil 1: </b> {point.q1}<br/>' +
						'<b>- Mediana: </b> {point.median}<br/>' +
						'<b>- Quartil 3: </b> {point.q3} <br/>' +
						'<b>- Máximo: </b> {point.high}<br/>',
				},
			},
			{
				type: 'scatter',
				name: 'Serie',
				id: 'serie',
				color: '#009E27',
				data,
				marker: { symbol: 'circle' },
			},
		]
	},
	medicoesHistoricasFiltradas({
		medicoesHistoricas,
		colunaAtributo,
		fazendasSelecionadas,
		talhoesSelecionados,
		parcelasSelecionadas,
	}) {
		const { key: key_fazenda } = colunaAtributo.find((coluna) => coluna.value === 'fazenda')
		const { key: key_talhao } = colunaAtributo.find((coluna) => coluna.value === 'talhao')
		const { key: key_parcela } = colunaAtributo.find((coluna) => coluna.value === 'parcela')

		return medicoesHistoricas
			.filter((medicao) =>
				fazendasSelecionadas.length ? fazendasSelecionadas.includes(medicao[key_fazenda]) : medicao,
			)
			.filter((medicao) =>
				talhoesSelecionados.length ? talhoesSelecionados.includes(medicao[key_talhao]) : medicao,
			)
			.filter((medicao) =>
				parcelasSelecionadas.length ? parcelasSelecionadas.includes(medicao[key_parcela]) : medicao,
			)
	},
	fazendas({ medicoesHistoricas, colunaAtributo }) {
		const { key: key_fazenda } = colunaAtributo.find((coluna) => coluna.value === 'fazenda')
		return [...new Set(medicoesHistoricas.map((medicao) => medicao[key_fazenda]))].map(
			(fazenda) => {
				return {
					id: fazenda,
					Nome: fazenda,
					[i18n.t('farm')]: fazenda,
				}
			},
		)
	},
	talhoes({ medicoesHistoricas, colunaAtributo, fazendasSelecionadas }) {
		const { key: key_talhao } = colunaAtributo.find((coluna) => coluna.value === 'talhao')
		const { key: key_fazenda } = colunaAtributo.find((coluna) => coluna.value === 'fazenda')

		const medicoes = medicoesHistoricas.filter((medicao) =>
			fazendasSelecionadas.includes(medicao[key_fazenda]),
		)

		return [...new Set(medicoes.map((medicao) => medicao[key_talhao]))].map((talhao) => {
			return {
				id: talhao,
				Nome: talhao,
				[i18n.t('section')]: talhao,
			}
		})
	},
	parcelas({ medicoesHistoricas, colunaAtributo, fazendasSelecionadas, talhoesSelecionados }) {
		const { key: key_parcela } = colunaAtributo.find((coluna) => coluna.value === 'parcela')
		const { key: key_talhao } = colunaAtributo.find((coluna) => coluna.value === 'talhao')
		const { key: key_fazenda } = colunaAtributo.find((coluna) => coluna.value === 'fazenda')

		const medicoes = medicoesHistoricas
			.filter((medicao) => fazendasSelecionadas.includes(medicao[key_fazenda]))
			.filter((medicao) => talhoesSelecionados.includes(medicao[key_talhao]))

		return [...new Set(medicoes.map((medicao) => medicao[key_parcela]))].map((parcela) => {
			return {
				id: parcela,
				Nome: parcela,
				[i18n.t('plot')]: parcela,
			}
		})
	},
	VTCC_MENOR_VTSC({ colunaAtributo, medicoesHistoricas }) {
		const { key: key_vtcc } = colunaAtributo.find((coluna) => coluna.value === 'vtcc')
		const { key: key_vtsc } = colunaAtributo.find((coluna) => coluna.value === 'vtsc')

		return medicoesHistoricas.filter((medicao) => {
			if (key_vtcc === 'Não Atribuir' && key_vtsc === 'Não Atribuir') {
				return false
			}

			return Number(medicao[key_vtcc]) < Number(medicao[key_vtsc])
		})
	},
	VICC_MENOR_VISC({ colunaAtributo, medicoesHistoricas }) {
		const { key: key_vicc } = colunaAtributo.find((coluna) => coluna.value === 'vicc')
		const { key: key_visc } = colunaAtributo.find((coluna) => coluna.value === 'visc')

		return medicoesHistoricas.filter((medicao) => {
			if (key_vicc === 'Não Atribuir' && key_visc === 'Não Atribuir') {
				return false
			}
			return Number(medicao[key_vicc]) < Number(medicao[key_visc])
		})
	},
	DAP_INCONSISTENTE({ colunaAtributo, medicoesHistoricas }) {
		const { key: key_dap_min } = colunaAtributo.find((coluna) => coluna.value === 'dap_min')
		const { key: key_dap_med } = colunaAtributo.find((coluna) => coluna.value === 'dap_med')
		const { key: key_dap_max } = colunaAtributo.find((coluna) => coluna.value === 'dap_max')

		return medicoesHistoricas.filter((medicao) => {
			if (
				key_dap_min === 'Não Atribuir' ||
				key_dap_med === 'Não Atribuir' ||
				key_dap_max === 'Não Atribuir'
			) {
				return false
			}

			return (
				Number(medicao[key_dap_max]) < Number(medicao[key_dap_med]) &&
				Number(medicao[key_dap_med]) > Number(medicao[key_dap_min])
			)
		})
	},
	HT_INCONSISTENTE({ colunaAtributo, medicoesHistoricas }) {
		const { key: key_ht_min } = colunaAtributo.find((coluna) => coluna.value === 'h_min')
		const { key: key_ht_med } = colunaAtributo.find((coluna) => coluna.value === 'h_med')
		const { key: key_ht_max } = colunaAtributo.find((coluna) => coluna.value === 'h_max')

		return medicoesHistoricas.filter((medicao) => {
			if (
				key_ht_min === 'Não Atribuir' ||
				key_ht_med === 'Não Atribuir' ||
				key_ht_max === 'Não Atribuir'
			) {
				return false
			}
			return (
				Number(medicao[key_ht_max]) < Number(medicao[key_ht_med]) &&
				Number(medicao[key_ht_med]) > Number(medicao[key_ht_min])
			)
		})
	},

	DG_MENOR_DAP_MED({ colunaAtributo, medicoesHistoricas }) {
		const { key: key_dg } = colunaAtributo.find((coluna) => coluna.value === 'dg')
		const { key: key_dap_med } = colunaAtributo.find((coluna) => coluna.value === 'dap_med')

		return medicoesHistoricas.filter((medicao) => {
			if (key_dg === 'Não Atribuir') {
				return false
			}
			return Number(medicao[key_dg]) < Number(medicao[key_dap_med])
		})
	},
	HDOM_MENOR_HMED({ colunaAtributo, medicoesHistoricas }) {
		const { key: key_hdom } = colunaAtributo.find((coluna) => coluna.value === 'h_dom')
		const { key: key_ht_med } = colunaAtributo.find((coluna) => coluna.value === 'h_med')

		return medicoesHistoricas.filter((medicao) => {
			if (key_hdom === 'Não Atribuir' || key_ht_med === 'Não Atribuir') {
				return false
			}

			return Number(medicao[key_hdom]) < Number(medicao[key_ht_med])
		})
	},
	SIGLA_INVENTARIO({ colunaAtributo, medicoesHistoricas }) {
		const { key: key_tipo_inventario } = colunaAtributo.find(
			(coluna) => coluna.value === 'tipoInventario',
		)

		return medicoesHistoricas.filter(
			(medicao) => !['IPC', 'IFI', 'IFC'].includes(medicao[key_tipo_inventario]),
		)
	},
	SEM_MATERIAL_GENETICO({ colunaAtributo, medicoesHistoricas, materiaisGeneticos }) {
		const { key: key_material_genetico } = colunaAtributo.find(
			(coluna) => coluna.value === 'materialGenetico',
		)

		return medicoesHistoricas.filter((medicao) => {
			if (key_material_genetico === 'Não Atribuir') {
				return false
			}

			return !materiaisGeneticos.includes(medicao[key_material_genetico])
		})
	},
	SEM_ESPECIE({ colunaAtributo, medicoesHistoricas, especies }) {
		const { key: key_especies } = colunaAtributo.find((coluna) => coluna.value === 'especie')

		return medicoesHistoricas.filter((medicao) => {
			if (key_especies === 'Não Atribuir') {
				return false
			}

			return !especies.includes(medicao[key_especies])
		})
	},
	OBRIGATORIOS_NAO_MAPEADOS({ colunaAtributo }, { colunasMapeamento }) {
		return colunasMapeamento.filter(
			(coluna) =>
				coluna.obrigatorio &&
				colunaAtributo.find((c) => c.value === coluna.key).key === 'Não Atribuir',
		)
	},
	OBRIGATORIOS_PROCESSAMENTO({ colunaAtributo }, { colunasProcessamento }) {
		return colunasProcessamento.every(
			(coluna) => colunaAtributo.find((c) => c.value === coluna.key).key === 'Não Atribuir',
		)
	},
	createBaseHistoricaBody({ colunaAtributo, medicoesHistoricas, descricao }) {
		const { key: dataMedicao } = colunaAtributo.find((coluna) => coluna.value === 'dataMedicao')
		const { key: parcela } = colunaAtributo.find((coluna) => coluna.value === 'parcela')

		const datasMedicao = medicoesHistoricas
			.map((medicao) => new Date(medicao[dataMedicao].split('/').reverse().join('/')).getTime())
			.sort((a, b) => a - b)

		const parcelas = [...new Set(medicoesHistoricas.map((medicao) => medicao[parcela]))].length

		const colunaParcela = { coluna: 'ParcelaId', key: 'ParcelaId', value: 'ParcelaId' }
		const colunas = [...colunaAtributo, colunaParcela]

		const medicoes = medicoesHistoricas.map((medicao) => {
			const entrada = colunas.map(({ value, key }) => {
				let valor = medicao[key]

				if ((value === 'dataMedicao' || value === 'dataPlantio') && typeof valor === 'string') {
					const partes = valor.split('/')
					if (partes.length === 3) {
						valor = new Date(partes.reverse().join('/')).toISOString().split('T')[0]
					}
				}

				return [value, valor]
			})

			return Object.fromEntries(entrada)
		})

		return {
			baseHistorica: {
				descricao,
				periodo: `${new Date(datasMedicao[0]).toLocaleDateString()} - ${new Date(datasMedicao[datasMedicao.length - 1]).toLocaleDateString()}`,
				parcelas,
			},
			medicoesHistoricas: medicoes,
		}
	},
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
}

Vue.use(Vuex)

const store = new Vuex.Store({ state, actions, mutations, getters })

export const useDadosHistoricosStore = () => store
